<template>
  <div class="bg-white p-3 border-b border-gray-200">
    <div
      :class="[
        'flex items-center justify-between flex-wrap',
        fullWidth ? 'w-full' : 'mx-auto max-w-7xl'
      ]"
    >
      <ul class="flex items-center">
        <li
          v-for="(navItem, index) in navItems"
          :key="index"
          :class="{ 'hidden sm:inline-flex': shouldHideSecondItem(index) }"
          class="inline-flex items-center overflow-hidden relative"
        >
          <!-- Conditional rendering for the last item -->
          <template v-if="index === navItems.length - 1">
            <span class="text-sm text-blue-400 whitespace-nowrap overflow-hidden text-ellipsis">
              {{ navItem.label }}
            </span>
          </template>
          <template v-else>
            <!-- Show home icon for the first item -->
            <nuxt-link
              :to="navItem.name"
              class="text-sm text-gray-600 hover:text-blue-500 "
              :class="{ 'whitespace-nowrap overflow-hidden text-ellipsis': index !== 0 }"
            >
              <template v-if="index === 0">
                <svg
                  class="w-5 h-auto fill-current mx-2 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="#000000"
                >
                  <path d="M0 0h24v24H0V0z" fill="none" />
                  <path
                    d="M10 19v-5h4v5c0 .55.45 1 1 1h3c.55 0 1-.45 1-1v-7h1.7c.46 0 .68-.57.33-.87L12.67 3.6c-.38-.34-.96-.34-1.34 0l-8.36 7.53c-.34.3-.13.87.33.87H5v7c0 .55.45 1 1 1h3c.55 0 1-.45 1-1z"
                  />
                </svg>
              </template>
              <!-- Show the label for other items -->
              <template v-else>
                {{ navItem.label }}
              </template>
            </nuxt-link>
          </template>

          <!-- Show arrow between items except after the last one -->
          <svg
            v-if="index !== navItems.length - 1"
            class="w-5 h-auto fill-current mx-2 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
          >
            <path d="M0 0h24v24H0V0z" fill="none" />
            <path
              d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z"
            />
          </svg>
        </li>
      </ul>

      <!-- Cart Icon and Dropdown -->
      <div 
        class="relative ml-auto" 
      >
        <button 
          type="button" 
          @click="toggleDropdown" 
          class="relative flex items-center text-gray-600 hover:text-gray-800 focus:outline-none"
          aria-label="Toggle cart panel"
        >
          <i class="fas fa-shopping-cart text-xl"></i>
          <span 
            v-if="!isLoading && cartItemCount > 0"
            class="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
          >
            {{ cartItemCount }}
          </span>
          <span 
            v-else-if="isLoading"
            class="absolute -top-2 -right-2 bg-gray-400 rounded-full h-5 w-5 flex items-center justify-center animate-pulse"
          >
            <i class="fas fa-spinner fa-spin text-white text-xs"></i>
          </span>
        </button>

        <!-- Dropdown Panel -->
        <div 
          v-show="isDropdownVisible" 
          class="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-xl z-50 border border-gray-200"
        >
          <div v-if="isLoading && cartItems.length === 0" class="p-4 text-center text-sm text-gray-500">กำลังโหลด...</div>
          <div v-else-if="!isLoading && cartItems.length === 0" class="p-4 text-center text-sm text-gray-500">ตะกร้าสินค้าของคุณว่างเปล่า</div>
          <div v-else class="p-4"> 
            <h3 class="text-sm font-semibold text-gray-800 mb-3">สินค้าในตะกร้า</h3>
            <ul class="space-y-3 max-h-60 overflow-y-auto pr-2">
              <li v-for="item in dropdownItems" :key="item.sku" class="flex items-start space-x-3 border-b border-gray-100 pb-3 last:border-b-0 last:pb-0">
                <img :src="item.imageUrl || item.mainImageUrl || 'https://placehold.co/50x50'" alt="Product Image" class="w-12 h-12 object-cover rounded flex-shrink-0">
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-medium text-gray-800 truncate">{{ item.description }}</p>
                  <p class="text-xs text-gray-500">จำนวน: {{ item.quantity }}</p>
                  <p class="text-xs font-semibold text-indigo-600">฿{{ item.displayPrice?.toFixed(2) }}</p>
                </div>
              </li>
              <li v-if="cartItems.length > dropdownItemLimit" class="text-center text-xs text-gray-500 pt-2">
                  ...และอีก {{ cartItems.length - dropdownItemLimit }} รายการ
              </li>
            </ul>
            <div class="mt-4 pt-4 border-t border-gray-200">
                <div class="flex justify-between items-center mb-3">
                    <span class="text-sm font-medium text-gray-900">รวม ({{ cartItemCount }} ชิ้น):</span>
                    <span class="text-sm font-bold text-red-600">฿{{ totalDropdownPrice.toFixed(2) }}</span>
                </div>
              <button 
                  type="button" 
                  @click="goToCartAndHide"
                  class="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center text-sm font-bold py-2 px-4 rounded-md transition duration-150"
                >
                ดูตะกร้าสินค้า
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- End Cart Icon -->

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useNuxtApp } from '#app';
import { useRouter } from 'vue-router';

const props = defineProps({
  navItems: {
    type: Array,
    required: true,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
});

const isClient = ref(false);
const { $DSS } = useNuxtApp();
const router = useRouter();

const cartItems = ref([]);
const isLoading = ref(false);
const error = ref(null);
const isDropdownVisible = ref(false);
const dropdownItemLimit = 5;

const fetchCart = async () => {
  if (isDropdownVisible.value && cartItems.value.length === 0) {
      isLoading.value = true;
  }
  error.value = null;
  try {
    const response = await $DSS('store/cart');

    if (response && response.success && Array.isArray(response.data)) {
      let allProcessedItems = [];
      response.data.forEach(storeGroup => {
        const storeId = storeGroup.ownerDetails?._id || 'unknown_store';
        const storeName = storeGroup.ownerDetails?.name || 'Unknown Store';

        if (Array.isArray(storeGroup.items)) {
          const processedItemsInGroup = storeGroup.items.map(item => {
            let displayPrice = item.retailPrice;
            if (item.salePrice !== undefined && item.salePrice !== null && item.salePrice > 0 && item.salePrice < item.retailPrice) {
              displayPrice = item.salePrice;
            }
            return {
              ...item,
              storeId: storeId,
              storeName: storeName,
              displayPrice: displayPrice
            };
          });
          allProcessedItems = allProcessedItems.concat(processedItemsInGroup);
        }
      });
      cartItems.value = allProcessedItems;
    } else if (response && response.status === 404) {
      cartItems.value = [];
    } else {
      console.error('Failed to fetch cart data for icon:', response?.error || 'Unknown error');
      error.value = response?.error || 'Failed to fetch cart data';
    }
  } catch (err) {
    console.error('Error fetching cart for icon:', err);
    error.value = err.message;
  } finally {
    // Always set loading to false in finally, regardless of dropdown visibility
    isLoading.value = false;
  }
};

const cartItemCount = computed(() => {
  return cartItems.value.reduce((total, item) => total + (item.quantity || 0), 0);
});

const dropdownItems = computed(() => {
  return cartItems.value.slice(0, dropdownItemLimit);
});

const totalDropdownPrice = computed(() => {
    return cartItems.value.reduce((total, item) => {
        return total + ((item.displayPrice || 0) * (item.quantity || 0));
    }, 0);
});

const toggleDropdown = () => {
  const newValue = !isDropdownVisible.value;
  console.log(`[Breadcrumbs] Toggling dropdown. Current: ${isDropdownVisible.value}, New: ${newValue}`);
  isDropdownVisible.value = newValue;
  if (isDropdownVisible.value) {
    console.log('[Breadcrumbs] Dropdown opened, fetching cart...');
    fetchCart();
  } else {
    console.log('[Breadcrumbs] Dropdown closed.');
  }
};

const goToCartAndHide = () => {
  router.push('/shop/cart');
  isDropdownVisible.value = false;
};

onMounted(() => {
  isClient.value = true;
  window.addEventListener('resize', onResize);
  fetchCart();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize);
});

const isMobile = computed(() => {
  return isClient.value && window.innerWidth < 640;
});

const shouldHideSecondItem = (index) => {
  return index === 1 && props.navItems.length > 3 && isMobile.value;
};

const onResize = () => {
  isClient.value = true;
};
</script>

<style scoped>
.max-h-60 {
    max-height: 15rem;
}
</style>
